import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Paths from "../router/Paths";
import adminClaimReward from "../controllers/account/adminClaimReward";
import { useDispatch } from "react-redux";
import { setClaimed } from "../reducers/userReducer";
import { enqueueSnackbar } from "notistack";

const DIGITS = 4;

export default function AdminValidateContentSection() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [vals, setVals] = useState(Array(DIGITS).fill(""));
  const inputsRef = useRef([]);
  const [submitting, setSubmitting] = useState(false);

  const code = useMemo(() => vals.join(""), [vals]);
  const ready = code.length === DIGITS && vals.every((v) => v !== "");

  const focusAt = (i) => inputsRef.current[i]?.focus();

  const onChange = (i, v) => {
    // allow only alphanumeric; uppercase; 1 char
    const only = v
      .replace(/[^A-Za-z0-9]/g, "")
      .toUpperCase()
      .slice(0, 1);
    setVals((prev) => {
      const next = [...prev];
      next[i] = only;
      return next;
    });
    if (only && i < DIGITS - 1) focusAt(i + 1);
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      if (vals[i]) {
        setVals((prev) => {
          const next = [...prev];
          next[i] = "";
          return next;
        });
      } else if (i > 0) {
        focusAt(i - 1);
        setVals((prev) => {
          const next = [...prev];
          next[i - 1] = "";
          return next;
        });
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      focusAt(i - 1);
    } else if (e.key === "ArrowRight" && i < DIGITS - 1) {
      e.preventDefault();
      focusAt(i + 1);
    }
  };

  const onPaste = (i, e) => {
    e.preventDefault();
    const text = (e.clipboardData.getData("text") || "")
      .replace(/[^A-Za-z0-9]/g, "")
      .toUpperCase();
    if (!text) return;
    const chars = text.slice(0, DIGITS - i).split("");
    setVals((prev) => {
      const next = [...prev];
      chars.forEach((c, idx) => (next[i + idx] = c));
      return next;
    });
    const targetIdx = Math.min(i + chars.length, DIGITS - 1);
    focusAt(targetIdx);
  };

  const onContinue = async () => {
    if (!ready || submitting) return;
    setSubmitting(true);
    try {
      // TODO: call admin-verify endpoint with `code`
      // await verifyGiftCode(code)
      const res = await adminClaimReward(code)?.finally(() =>
        setSubmitting(false)
      );
      const result = res?.data?.data;
      if (result) {
        dispatch(setClaimed(result));
        navigate(Paths.adminClaim);
      } else {
        enqueueSnackbar(
          res?.msg || "Something went wrong. Please try again later.",
          { variant: "error" }
        );
      }
      // console.log({ res });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="w-full py-6">
      <h2 className="text-2xl font-extrabold text-center">
        Validate Gift Code
      </h2>
      <p className="mt-2 text-center text-gray-600 max-w-md mx-auto">
        Enter the customer’s gift code below to verify its validity. Once
        confirmed, you can mark the prize as claimed and hand it over.
      </p>

      {/* Boxes */}
      <div className="mt-6 grid grid-cols-4 gap-3">
        {Array.from({ length: DIGITS }).map((_, i) => (
          <input
            key={i}
            ref={(el) => (inputsRef.current[i] = el)}
            inputMode="text"
            pattern="[A-Za-z0-9]*"
            maxLength={1}
            value={vals[i]}
            onChange={(e) => onChange(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
            onPaste={(e) => onPaste(i, e)}
            className="h-20 rounded-md border border-gray-300 bg-gray-100 text-center text-2xl uppercase
                       focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
          />
        ))}
      </div>

      {/* CTA */}
      <div className="mt-6">
        <button
          type="button"
          onClick={onContinue}
          disabled={!ready || submitting}
          className={`w-full rounded-md py-3 text-white font-semibold shadow-sm flex items-center justify-center active:scale-[.99]
            bg-gradient-to-r from-blue-500 to-blue-600
            ${!ready || submitting ? "opacity-60 cursor-not-allowed" : ""}`}
        >
          {submitting ? (
            // simple inline spinner
            <span className="inline-block h-5 w-5 border-2 border-white/60 border-t-white rounded-full animate-spin" />
          ) : (
            "Confirm Code"
          )}
        </button>
      </div>
    </section>
  );
}
